// Last-known-position cache (Phase 5 Step 17d).
//
// Pure module — no PixiJS imports. Mitigates the one-frame HP-bar
// flicker that happens when a combatant dies on a tick:
//   - expeditionPositions for the same tick excludes them (alive=0).
//   - The client already wrote HP=0 via damageTaken.
//   - The HP-bar resolver falls back to zonePlayers and snaps the bar
//     to the open-world roster coords, which are stale/wrong.
//
// Fix precedence: fresh authoritative position > cached. Cached wins
// ONLY when the resolver returns null AND the cached entry is within
// POSITION_CACHE_TTL_MS. Stale entries are not pruned on read (cheap);
// the capped Map size + the once-per-frame refresh keeps memory bounded.

export const POSITION_CACHE_TTL_MS = 2000; // contracts:allow-literal — 2s last-known-position TTL

export type CachedPosition = { x: number; y: number; cachedAt: number };

export type PositionCache = Map<string, CachedPosition>;

export function createPositionCache(): PositionCache {
  return new Map();
}

export function recordPosition(
  cache: PositionCache,
  entityId: string,
  pos: { x: number; y: number },
  now: number,
): void {
  cache.set(entityId, { x: pos.x, y: pos.y, cachedAt: now });
}

// Resolve with cache fallback: if `fresh` is non-null, cache it and
// return it. If `fresh` is null but a cached entry exists within the
// TTL, return the cached coords. Otherwise null.
export function resolveWithCache(
  cache: PositionCache,
  entityId: string,
  fresh: { x: number; y: number } | null,
  now: number,
): { x: number; y: number } | null {
  if (fresh !== null) {
    recordPosition(cache, entityId, fresh, now);
    return fresh;
  }
  const cached = cache.get(entityId);
  if (cached === undefined) return null;
  if (now - cached.cachedAt > POSITION_CACHE_TTL_MS) return null;
  return { x: cached.x, y: cached.y };
}
